import moment from 'moment';
import { getSupabase, isSupabaseConfigured } from './supabase';
import { DEAL_PARTY_ROLES } from '../constants/crmConstants';
// NOTE: firebase (./init-firebase) is loaded lazily inside fetchUserFields —
// importing it at module scope initializes Firebase Auth, which breaks any
// jest suite that imports this util.

/**
 * Shared client-side template variable resolution for email + SMS.
 *
 * Mirrors the backend resolvers (functions/CRM/email/emailUtils.js
 * applyEmailTemplate and functions/CRM/dialpad/smsVariables.js
 * resolveVariables) so previews match what actually gets sent: every
 * scalar contact/deal field is registered under both its flat camelCase
 * key ({{firstName}}) and its namespaced key ({{contact.firstName}}),
 * plus formatted convenience aliases, sender variables, and the deal-team
 * role namespaces ({{owner.*}}, {{primaryOwner.*}}, {{csc.*}},
 * {{titleCompany.*}}, ... — backend mirror:
 * functions/CRM/utils/dealTeamContext.js).
 *
 * Accepts contact/deal objects in either the app's camelCase shape
 * (mapDealFromDb — including the nested propertyAddress object) or raw
 * snake_case Supabase rows.
 */

function snakeToCamelKey(key) {
  return key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

/** Shallow-normalize an object's keys to camelCase. */
function normalizeKeys(obj) {
  if (!obj || typeof obj !== 'object') return {};
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    result[snakeToCamelKey(key)] = value;
  }
  return result;
}

function registerScalars(vars, namespace, normalized) {
  for (const [key, value] of Object.entries(normalized)) {
    if (value !== null && value !== undefined && typeof value !== 'object') {
      vars[key] = String(value);
      vars[`${namespace}.${key}`] = String(value);
    }
  }
}

function register(vars, flatKey, namespacedKey, value) {
  vars[flatKey] = value;
  vars[namespacedKey] = value;
}

/**
 * Build the full {{variable}} → value map for a contact/deal/sender trio.
 *
 * @param {Object} params
 * @param {Object} [params.contact] - camelCase contact or snake_case crm_contacts row
 * @param {Object} [params.deal] - camelCase deal or snake_case crm_deals row
 * @param {Object} [params.sender] - { name, email, phone }
 * @param {Object} [params.dealTeam] - { owner, roles } from fetchDealTeamVariableContext
 * @returns {Object} map of variable keys (flat + namespaced) to string values
 */
export function buildVariableMap({ contact, deal, sender, dealTeam } = {}) {
  const vars = {};

  if (contact) {
    const c = normalizeKeys(contact);
    registerScalars(vars, 'contact', c);

    const fullAddress = [c.mailingStreet, c.mailingCity, c.mailingState, c.mailingZip]
      .filter(Boolean)
      .join(', ');
    if (fullAddress) register(vars, 'fullAddress', 'contact.fullAddress', fullAddress);
  }

  if (deal) {
    const d = normalizeKeys(deal);
    registerScalars(vars, 'deal', d);

    // Formatted convenience aliases (override raw values, like the backend)
    const rawAmount = d.amount ?? d.loanAmount;
    const fmtAmount = rawAmount ? `$${Number(rawAmount).toLocaleString()}` : '';
    register(vars, 'amount', 'deal.amount', fmtAmount);
    register(vars, 'loanAmount', 'deal.loanAmount', fmtAmount);

    // propertyAddress: nested object (app shape) or flat columns (DB row shape)
    const nested = d.propertyAddress && typeof d.propertyAddress === 'object' ? d.propertyAddress : null;
    const propAddr = [
      nested ? nested.street : d.propertyStreet,
      nested ? nested.city : d.propertyCity,
      nested ? nested.state : d.propertyState,
      nested ? nested.zip : d.propertyZip,
    ].filter(Boolean).join(', ');
    register(vars, 'propertyAddress', 'deal.propertyAddress', propAddr);

    const closingDate = d.estimatedClosingDate
      ? moment(d.estimatedClosingDate).format('MMM D, YYYY')
      : '';
    register(vars, 'estimatedClosingDate', 'deal.estimatedClosingDate', closingDate);
  }

  if (sender) {
    const name = sender.name || '';
    register(vars, 'senderName', 'sender.name', name);
    register(vars, 'senderEmail', 'sender.email', sender.email || '');
    register(vars, 'senderPhone', 'sender.phone', sender.phone || '');
    register(vars, 'bookingLink', 'sender.bookingLink', sender.bookingLink || '');
    register(vars, 'websiteLink', 'sender.websiteLink', sender.websiteLink || '');
    vars.loanOfficerName = name;
  }

  if (dealTeam) {
    const put = (ns, fields) => {
      for (const [field, value] of Object.entries(fields || {})) {
        if (value === null || value === undefined || typeof value === 'object') continue;
        vars[`${ns}.${field}`] = String(value);
      }
    };
    if (dealTeam.owner) {
      put('owner', dealTeam.owner);
      put('primaryOwner', dealTeam.owner);
    }
    for (const [key, fields] of Object.entries(dealTeam.roles || {})) {
      put(key, fields);
    }
  }

  return vars;
}

// ---------------------------------------------------------------------------
// Deal team context (Phase 5) — frontend mirror of
// functions/CRM/utils/dealTeamContext.js loadDealTeamContext()
// ---------------------------------------------------------------------------

/** 'buyer-agent-tc' -> 'buyerAgentTc' (fallback for roles without a variableKey). */
function toCamelKey(slug) {
  return String(slug || '')
    .trim()
    .replace(/[^a-zA-Z0-9]+([a-zA-Z0-9])/g, (_, c) => c.toUpperCase())
    .replace(/[^a-zA-Z0-9]/g, '')
    .replace(/^[A-Z]/, (c) => c.toLowerCase());
}

function normalizeUserName(raw) {
  if (!raw) return '';
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object') {
    return raw.fullName || `${raw.givenName || ''} ${raw.familyName || ''}`.trim();
  }
  return String(raw);
}

/** Internal person fields from a Firestore users doc (backend parity). */
function internalPersonFields(uid, u) {
  const name = normalizeUserName(u.Name || u.name || u.displayName);
  const parts = name.split(/\s+/).filter(Boolean);
  return {
    id: uid,
    name,
    firstName: parts[0] || '',
    lastName: parts.length > 1 ? parts.slice(1).join(' ') : '',
    email: u.Email || u.email || '',
    phone: u.Phone || u.phone || '',
    nmls: u.NMLS || u.nmls || '',
    title: u.Title || u.title || '',
    bookingLink: u.defaultBookingUrl || '',
    websiteLink: u.website || '',
  };
}

/** External person fields from a crm_contacts row (backend parity). */
function externalPersonFields(c) {
  const fullName = [c.first_name, c.last_name].filter(Boolean).join(' ');
  return {
    id: c.id,
    name: fullName || c.company || '',
    firstName: c.first_name || '',
    lastName: c.last_name || '',
    fullName,
    email: c.email || '',
    phone: c.phone || '',
    company: c.company || '',
  };
}

const UNOWNED_UIDS = new Set(['', 'unassigned', 'company']);
const isRealUid = (uid) => typeof uid === 'string' && uid.length > 0 && !UNOWNED_UIDS.has(uid);

async function fetchUserFields(uid) {
  try {
    const [{ db }, { doc, getDoc }] = await Promise.all([
      import('./init-firebase'),
      import('firebase/firestore'),
    ]);
    const snap = await getDoc(doc(db, 'users', uid));
    return snap.exists() ? internalPersonFields(uid, snap.data() || {}) : null;
  } catch (err) {
    console.warn('[templateVariables] User fetch failed:', err);
    return null;
  }
}

/**
 * Fetch the deal-team variable context for one deal — used so previews can
 * resolve {{owner.*}} / role namespaces exactly like the backend will.
 * Best-effort: any failure yields partial/empty context (tags stay visible
 * while editing; the backend re-resolves at send time).
 *
 * @param {string|null} dealId
 * @param {Object} [options]
 * @param {string} [options.dealOwnerId] crm_deals.owner_id (primary owner)
 * @param {string} [options.contactOwnerId] fallback when no deal owner
 * @param {Array}  [options.dealPartyRoles] role config (from useCrmSettings);
 *   defaults to the canonical DEAL_PARTY_ROLES constant
 * @returns {Promise<{ owner: Object|null, roles: Object }>}
 */
export async function fetchDealTeamVariableContext(dealId, {
  dealOwnerId = null,
  contactOwnerId = null,
  dealPartyRoles = DEAL_PARTY_ROLES,
} = {}) {
  const result = { owner: null, roles: {} };

  const ownerUid = isRealUid(dealOwnerId) ? dealOwnerId : (isRealUid(contactOwnerId) ? contactOwnerId : null);
  if (ownerUid) {
    result.owner = await fetchUserFields(ownerUid);
  }

  if (!dealId || !isSupabaseConfigured()) return result;

  try {
    const supabase = getSupabase();
    const { data: rows, error } = await supabase
      .from('crm_deal_participants')
      .select('contact_id, user_id, role, is_primary, created_at')
      .eq('deal_id', dealId)
      .order('is_primary', { ascending: false })
      .order('created_at', { ascending: true });
    if (error) throw error;
    if (!rows?.length) return result;

    const roleMap = {};
    for (const role of dealPartyRoles || []) {
      if (role?.value) {
        roleMap[role.value] = {
          variableKey: role.variableKey || toCamelKey(role.value),
          internal: role.internal === true,
        };
      }
    }

    const firstByRole = new Map();
    for (const row of rows) {
      if (!firstByRole.has(row.role)) firstByRole.set(row.role, row);
    }

    const contactIds = [...firstByRole.values()].map((r) => r.contact_id).filter(Boolean);
    const contactsById = new Map();
    if (contactIds.length > 0) {
      const { data: contacts } = await supabase
        .from('crm_contacts')
        .select('id, first_name, last_name, email, phone, company')
        .in('id', contactIds);
      for (const c of contacts || []) contactsById.set(c.id, c);
    }

    for (const [roleSlug, row] of firstByRole) {
      const spec = roleMap[roleSlug] || { variableKey: toCamelKey(roleSlug), internal: false };
      const key = spec.variableKey;
      if (!key || result.roles[key]) continue;

      if (row.user_id) {
        const fields = await fetchUserFields(row.user_id);
        if (fields) result.roles[key] = fields;
      } else if (row.contact_id && contactsById.has(row.contact_id)) {
        result.roles[key] = externalPersonFields(contactsById.get(row.contact_id));
      }
    }
  } catch (err) {
    console.warn('[templateVariables] Deal team fetch failed:', err);
  }

  return result;
}

/**
 * Conditional sections: {{#key}}content{{/key}} keeps `content` only when
 * vars[key] is non-empty; otherwise the whole block disappears. Mirrors
 * applyConditionalSections in functions/CRM/automations/contextHydrator.js —
 * keep the two in sync.
 */
function applyConditionalSections(text, vars) {
  const SECTION_RE = /\{\{#\s*([\w.]+)\s*\}\}([\s\S]*?)\{\{\/\s*\1\s*\}\}/g;
  let out = text;
  let prev;
  do {
    prev = out;
    out = out.replace(SECTION_RE, (match, key, inner) => {
      const value = (vars || {})[key];
      const hasValue = value !== undefined && value !== null && String(value).trim() !== '';
      return hasValue ? inner : '';
    });
  } while (out !== prev);
  return out;
}

/**
 * Replace {{variable}} tags in text using a map from buildVariableMap.
 * Also applies {{#key}}...{{/key}} conditional sections (final-render mode
 * only — while editing with stripUnknown=false the markers stay visible and
 * the backend applies them at send time).
 *
 * @param {string} text
 * @param {Object} vars
 * @param {Object} [options]
 * @param {boolean} [options.stripUnknown=true] - remove unresolved {{tags}}.
 *   Pass false while editing (or when a backend re-resolves at send time)
 *   so unknown tags stay visible instead of silently disappearing.
 */
export function resolveTemplateVariables(text, vars, { stripUnknown = true } = {}) {
  if (!text) return '';

  let resolved = stripUnknown ? applyConditionalSections(text, vars) : text;
  for (const [key, value] of Object.entries(vars || {})) {
    const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Exact-match (no whitespace tolerance) for parity with the backend resolvers
    resolved = resolved.replace(new RegExp(`\\{\\{${escapedKey}\\}\\}`, 'g'), value);
  }
  if (stripUnknown) {
    resolved = resolved.replace(/\{\{[^}]+\}\}/g, '');
  }
  return resolved;
}

/**
 * Picker entries for the "Deal Team" variable category — shared by
 * DynamicVariableInsertButton (email surfaces) and the Builder
 * DynamicVariablePicker so both stay in sync. Driven by role config
 * (useCrmSettings().dealPartyRoles) so admin-created roles appear
 * automatically; falls back to the canonical constant.
 *
 * Each role exposes its most useful fields; the resolvers support the full
 * field set (internal: name/firstName/lastName/email/phone/nmls/title/
 * bookingLink/websiteLink; external: adds fullName/company) for hand-typed
 * tags.
 *
 * @param {Array} [dealPartyRoles]
 * @returns {{ key: string, label: string }[]}
 */
export function buildDealTeamVariableEntries(dealPartyRoles = DEAL_PARTY_ROLES) {
  const entries = [
    { key: 'owner.firstName', label: 'Primary Owner — First Name' },
    { key: 'owner.name', label: 'Primary Owner — Full Name' },
    { key: 'owner.email', label: 'Primary Owner — Email' },
    { key: 'owner.phone', label: 'Primary Owner — Phone' },
    { key: 'owner.nmls', label: 'Primary Owner — NMLS' },
    { key: 'owner.bookingLink', label: 'Primary Owner — Booking Link' },
    { key: 'owner.websiteLink', label: 'Primary Owner — Website' },
  ];
  for (const role of dealPartyRoles || []) {
    if (!role?.value) continue;
    const key = role.variableKey || toCamelKey(role.value);
    // "CSC — Client Success Coordinator" -> "CSC" (keep labels scannable)
    const base = String(role.label || role.value).split('—')[0].trim();
    entries.push(
      { key: `${key}.firstName`, label: `${base} — First Name` },
      { key: `${key}.${role.internal ? 'name' : 'fullName'}`, label: `${base} — Full Name` },
      { key: `${key}.email`, label: `${base} — Email` },
      { key: `${key}.phone`, label: `${base} — Phone` },
    );
    if (!role.internal) {
      entries.push({ key: `${key}.company`, label: `${base} — Company` });
    }
  }
  return entries;
}

/**
 * Insert a {{tag}} at the cursor position of a textarea (or append when the
 * element isn't available), returning focus to the textarea afterwards.
 *
 * @param {Object} textareaRef - React ref to the <textarea> element
 * @param {string} currentValue - current controlled value
 * @param {string} tag - text to insert, e.g. "{{contact.firstName}}"
 * @param {Function} setValue - state setter for the controlled value
 */
export function insertTagIntoTextarea(textareaRef, currentValue, tag, setValue) {
  const el = textareaRef?.current;
  const value = currentValue || '';
  if (el) {
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? start;
    setValue(value.slice(0, start) + tag + value.slice(end));
    setTimeout(() => {
      el.focus();
      const pos = start + tag.length;
      el.setSelectionRange(pos, pos);
    }, 0);
  } else {
    setValue(value + tag);
  }
}
